/**
 * CacheManager
 * sessionStorage wrapper for product data with LRU eviction
 */
class CacheManager {
  // Use centralized config (loaded before this file)
  static get CACHE_PREFIX() {
    return typeof InfiniteScrollConfig !== "undefined"
      ? InfiniteScrollConfig.CACHE_PREFIX
      : "cache:";
  }
  static get MAX_CACHE_SIZE() {
    return typeof InfiniteScrollConfig !== "undefined"
      ? InfiniteScrollConfig.MAX_CACHE_SIZE
      : 4 * 1024 * 1024;
  }
  static get HISTORY_KEY() {
    return typeof HistoryConfig !== "undefined"
      ? HistoryConfig.HISTORY_KEY
      : "infiniteScroll:historyStack";
  }
  static get RESTORE_FLAG_KEY() {
    return typeof HistoryConfig !== "undefined"
      ? HistoryConfig.RESTORE_FLAG_KEY
      : "infiniteScroll:restoreFlag";
  }

  /**
   * Get cached data for a collection/filter combination
   */
  static get(cacheKey) {
    try {
      const data = sessionStorage.getItem(cacheKey);
      if (!data) return null;

      const parsed = JSON.parse(data);

      // Update access timestamp for LRU
      parsed.lastAccessed = Date.now();
      sessionStorage.setItem(cacheKey, JSON.stringify(parsed));

      return parsed;
    } catch (e) {
      return null;
    }
  }

  /**
   * Store data in cache with automatic LRU eviction
   */
  static set(cacheKey, data) {
    try {
      const cacheData = {
        ...data,
        timestamp: Date.now(),
        lastAccessed: Date.now(),
      };

      const serialized = JSON.stringify(cacheData);

      // Evict old entries if needed
      while (
        this.getCacheSize() + serialized.length * 2 >
        this.MAX_CACHE_SIZE
      ) {
        const evicted = this.evictLRU();
        if (!evicted) break;
      }

      sessionStorage.setItem(cacheKey, serialized);
      return true;
    } catch (e) {
      this.evictLRU();
      try {
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({ ...data, timestamp: Date.now() })
        );
        return true;
      } catch {
        return false;
      }
    }
  }

  /**
   * Update specific fields in cached data
   */
  static update(cacheKey, updates) {
    const existing = this.get(cacheKey);
    if (!existing) return false;

    return this.set(cacheKey, { ...existing, ...updates });
  }

  /**
   * Append products to existing cache with deduplication
   */
  static appendProducts(cacheKey, feedType, newProducts, cursor, hasMore) {
    const existing = this.get(cacheKey);
    if (!existing) return false;

    const feed = existing[feedType] || {
      products: [],
      cursors: [],
      loadedCount: 0,
      totalCount: 0,
      filters: [],
      complete: false,
    };

    // DEDUPLICATE: Only add products not already in cache
    const existingIds = new Set(feed.products.map((p) => p.id));
    const uniqueNewProducts = newProducts.filter((p) => !existingIds.has(p.id));

    feed.products = [...feed.products, ...uniqueNewProducts];
    feed.cursors.push(cursor);
    feed.loadedCount = feed.products.length;
    feed.complete = !hasMore;

    existing[feedType] = feed;
    return this.set(cacheKey, existing);
  }

  /**
   * Update filters and total count in cache
   */
  static updateFilters(cacheKey, filters, totalCount) {
    const existing = this.get(cacheKey);
    if (!existing || !existing.collection) return false;

    existing.collection.filters = filters;
    existing.collection.totalCount = totalCount;

    return this.set(cacheKey, existing);
  }

  /**
   * Initialize cache entry for a new collection/filter combination
   * @param {string} cacheKey
   * @param {number} totalCount - Total products in collection
   * @param {Array} filters - Filter data from GraphQL
   */
  static initialize(cacheKey, totalCount = 0, filters = []) {
    const data = {
      collection: {
        products: [],
        loadedCount: 0,
        totalCount: totalCount,
        cursors: [],
        filters: filters,
        complete: false,
      },
      recommendations: {
        products: [],
        loadedCount: 0,
        totalCount: 0,
        cursors: [],
        filters: [],
        complete: false,
      },
    };

    this.set(cacheKey, data);
    return data;
  }

  /**
   * Delete a cache entry
   */
  static delete(cacheKey) {
    try {
      sessionStorage.removeItem(cacheKey);
    } catch (e) {
      // Error handled silently
    }
  }

  /**
   * Calculate total size of cache entries
   */
  static getCacheSize() {
    let size = 0;
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(this.CACHE_PREFIX)) {
        const value = sessionStorage.getItem(key);
        size += (key.length + (value ? value.length : 0)) * 2;
      }
    }
    return size;
  }

  /**
   * Evict least recently used cache entry
   */
  static evictLRU() {
    let oldestTime = Infinity;
    let oldestKey = null;

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(this.CACHE_PREFIX)) {
        try {
          const data = JSON.parse(sessionStorage.getItem(key));
          const accessTime = data.lastAccessed || data.timestamp || 0;
          if (accessTime < oldestTime) {
            oldestTime = accessTime;
            oldestKey = key;
          }
        } catch {
          oldestKey = key;
          break;
        }
      }
    }

    if (oldestKey) {
      sessionStorage.removeItem(oldestKey);
      return true;
    }

    return false;
  }

  /**
   * Clear all cache entries (preserves history stack)
   */
  static clearAll() {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(this.CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  }

  // ─────────────────────────────────────────────────────────────
  // PRODUCT DATA UTILITIES
  // ─────────────────────────────────────────────────────────────

  /**
   * Slim down product data to essential fields only
   */
  static slimProduct(product) {
    const variantNodes = product.variants?.nodes || [];
    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      featuredImage: product.featuredImage
        ? {
            url: product.featuredImage.url,
            altText: product.featuredImage.altText || "",
          }
        : null,
      price: product.priceRange?.minVariantPrice?.amount || null,
      compareAtPrice:
        product.compareAtPriceRange?.minVariantPrice?.amount || null,
      currency: product.priceRange?.minVariantPrice?.currencyCode || "JOD",
      // Custom sellable rule (NOT Shopify availableForSale): in stock when at
      // least one variant is sellable. Keeps JS-rendered (infinite-scroll) cards
      // consistent with the Liquid cards + PDP.
      available: CacheManager.computeSellableAvailable(product, variantNodes),
      vendor: product.vendor || null,
      firstVariantId: variantNodes[0]?.id || null,
    };
  }

  /**
   * Mirror of snippets/sellable-quantity-cap.liquid for a single Storefront API
   * variant: sellable when its computed cap is unlimited (continue selling with
   * no cap) or >= 1; out of stock when <= 0. quantityAvailable stands in for
   * inventory_quantity. Untracked inventory (or a token missing the inventory
   * scope) reports quantityAvailable as null — fall back to availableForSale so
   * we never blank out the whole grid.
   */
  static variantIsSellable(variant) {
    if (!variant) return false;

    const continueSelling = variant.contMeta?.value === "true";

    const capRaw = variant.capMeta?.value;
    const parsedCap =
      capRaw == null || capRaw === "" ? null : parseInt(capRaw, 10);
    const variantCap = Number.isFinite(parsedCap) ? parsedCap : null;

    if (continueSelling) {
      const rawDefault = window.storefront?.defaultSellableQuantityCap;
      const parsedDefault =
        rawDefault == null || rawDefault === ""
          ? null
          : parseInt(rawDefault, 10);
      const cap =
        variantCap != null
          ? variantCap
          : Number.isFinite(parsedDefault)
          ? parsedDefault
          : null;
      // null cap = unlimited.
      return cap == null || cap >= 1;
    }

    const qty = variant.quantityAvailable;
    const inventory =
      typeof qty === "number"
        ? qty
        : variant.availableForSale
        ? Infinity
        : 0;
    const cap = variantCap == null ? inventory : Math.min(variantCap, inventory);
    return cap >= 1;
  }

  static computeSellableAvailable(product, variantNodes) {
    if (!variantNodes || !variantNodes.length) {
      // No variant data (e.g. metafields not exposed): fall back to native.
      return product.availableForSale !== false;
    }
    return variantNodes.some((v) => CacheManager.variantIsSellable(v));
  }

  /**
   * Slim an array of products
   */
  static slimProducts(products) {
    return products.map((p) => this.slimProduct(p));
  }

  // ─────────────────────────────────────────────────────────────
  // HISTORY STACK
  // ─────────────────────────────────────────────────────────────
  // History stack structure:
  // [
  //   {
  //     url: string,
  //     restore: {
  //       pageScrollY: number,
  //       grids: {
  //         [gridId]: {
  //           carouselIndex, activeFeed, height, loadedCount, totalCount,
  //           products: Array  // Required: self-contained product snapshot for restoration
  //         }
  //       }
  //     },
  //     timestamp: number
  //   },
  //   ...
  // ]
  // CacheManager treats the stack as opaque - it only serializes/deserializes.
  // HistoryManager and GridInstance handle the semantics of products arrays.

  /**
   * Get the history stack
   */
  static getHistoryStack() {
    try {
      const data = sessionStorage.getItem(this.HISTORY_KEY);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  /**
   * Save the history stack
   */
  static setHistoryStack(stack) {
    try {
      sessionStorage.setItem(this.HISTORY_KEY, JSON.stringify(stack));
    } catch (e) {
      // Error handled silently
    }
  }

  /**
   * Get restore flag
   */
  static getRestoreFlag() {
    return sessionStorage.getItem(this.RESTORE_FLAG_KEY) === "true";
  }

  /**
   * Set restore flag
   */
  static setRestoreFlag(value) {
    sessionStorage.setItem(this.RESTORE_FLAG_KEY, value ? "true" : "false");
  }
}

// Export for module usage
if (typeof module !== "undefined" && module.exports) {
  module.exports = CacheManager;
}
